import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './FAQ.css';

interface FAQItem {
  id: number;
  questionNl: string;
  questionEn: string;
  answerNl: string | React.ReactNode;
  answerEn: string | React.ReactNode;
}

const faqData: FAQItem[] = [
  {
    id: 1,
    questionNl: 'In welke sectoren werven jullie?',
    questionEn: 'Which industries do you recruit for?',
    answerNl: (
      <>
        <p>Wij zijn gespecialiseerd in meerdere sectoren binnen de financiële dienstverlening, van junior tot senior posities. We werken samen met klanten in de volgende gebieden:</p>
        <div className="faq-industries">
          <div className="faq-industries-col">
            <a href="/jobs?discipline=Accounting">Accounting →</a>
            <a href="/jobs?discipline=Tax">Tax →</a>
          </div>
          <div className="faq-industries-col">
            <a href="/jobs?discipline=Audit">Audit →</a>
            <a href="/jobs?discipline=Legal">Legal →</a>
          </div>
        </div>
      </>
    ),
    answerEn: (
      <>
        <p>We specialise in multiple sectors within the financial services industry, recruiting at all levels, from graduate to senior roles. We partner with clients across the following industries:</p>
        <div className="faq-industries">
          <div className="faq-industries-col">
            <a href="/jobs?discipline=Accounting">Accounting →</a>
            <a href="/jobs?discipline=Tax">Tax →</a>
          </div>
          <div className="faq-industries-col">
            <a href="/jobs?discipline=Audit">Audit →</a>
            <a href="/jobs?discipline=Legal">Legal →</a>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 2,
    questionNl: 'Waarom heb ik een recruiter nodig?',
    questionEn: 'Why do I need a recruiter?',
    answerNl: 'Van carrière veranderen, een nieuwe medewerker aannemen of een team opbouwen zijn tijdrovende processen met veel administratieve en bewegende onderdelen. In plaats van uren te besteden aan het zoeken naar de perfecte functie of medewerker, doen wij het werk voor u. Wij nemen de stress en uitdagingen van recruitment uit handen en begeleiden u door het hele proces met onze marktleidende expertise. We hebben een uitgebreid netwerk van gewilde kandidaten en toonaangevende klanten om uitzonderlijke mensen te verbinden met unieke kansen.',
    answerEn: 'Changing careers, onboarding a new hire, or building out a team are time-consuming processes that can often involve a lot of administrative and moving components. Instead of spending hours searching for the perfect role or employee, we do the work for you. We take the stress and challenges out of recruitment and guide you through the entire process with our market-leading expertise. We have an extensive network of sought-after candidates and industry-leading clients to connect exceptional people with unique opportunities.',
  },
  {
    id: 3,
    questionNl: 'Wat is jullie werkwijze?',
    questionEn: 'What is your process?',
    answerNl: 'Ons proces begint met een persoonlijk kennismakingsgesprek om uw wensen en behoeften te begrijpen. Vervolgens doorzoeken we ons uitgebreide netwerk en selecteren we de beste kandidaten of vacatures. We begeleiden u door het hele sollicitatieproces, van eerste gesprekken tot contractonderhandelingen, en blijven ook na plaatsing beschikbaar voor ondersteuning.',
    answerEn: 'Our process begins with a personal introduction meeting to understand your wishes and needs. We then search through our extensive network and select the best candidates or vacancies. We guide you through the entire application process, from initial interviews to contract negotiations, and remain available for support even after placement.',
  },
  {
    id: 4,
    questionNl: 'Wat houdt No Cure, No Pay in?',
    questionEn: 'What does No Cure, No Pay mean?',
    answerNl: 'Met ons 100% No Cure, No Pay-principe loopt u geen financieel risico. Dit betekent dat u alleen betaalt wanneer wij succesvol een kandidaat plaatsen. Geen resultaat, geen kosten. Zo bieden we niet alleen kwaliteit, maar ook zekerheid.',
    answerEn: 'With our 100% No Cure, No Pay principle, you run no financial risk. This means you only pay when we successfully place a candidate. No result, no costs. This way we offer not only quality but also certainty.',
  },
  {
    id: 5,
    questionNl: 'Hoe kan ik contact opnemen?',
    questionEn: 'How can I get in touch?',
    answerNl: 'U kunt ons bereiken via telefoon (020 308 6271), e-mail (info@meijerknijnenberg.com) of door langs te komen op ons kantoor aan de Nieuwezijds voorburgwal 334-2, Amsterdam. We zijn altijd bereikbaar en staan klaar om uw vragen te beantwoorden.',
    answerEn: 'You can reach us by phone (020 308 6271), email (info@meijerknijnenberg.com), or by visiting our office at Nieuwezijds voorburgwal 334-2, Amsterdam. We are always available and ready to answer your questions.',
  },
];

const FAQ: React.FC = () => {
  const { language } = useLanguage();
  const [openId, setOpenId] = useState<number | null>(null);

  const toggleFAQ = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="faq-section" id="faq">
      <div className="faq-container">
        <div className="faq-left">
          <h2 className="faq-title">
            {language === 'nl' ? 'Heb je vragen?' : 'Need some clarity?'}
          </h2>
        </div>

        <div className="faq-right">
          {faqData.map((item) => (
            <div
              key={item.id}
              className={`faq-item ${openId === item.id ? 'active' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => toggleFAQ(item.id)}
                aria-expanded={openId === item.id}
              >
                <span>{language === 'nl' ? item.questionNl : item.questionEn}</span>
                <span className="faq-icon">{openId === item.id ? '−' : '+'}</span>
              </button>
              <div className="faq-answer">
                <div className="faq-answer-content">
                  {typeof (language === 'nl' ? item.answerNl : item.answerEn) === 'string' ? (
                    <p>{language === 'nl' ? item.answerNl : item.answerEn}</p>
                  ) : (
                    language === 'nl' ? item.answerNl : item.answerEn
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
